import type { Task, TaskReminderSettings } from '../types/task'

import { getActiveTenantKey, getTenantScopedKey } from '../offline/tenant'
import {
  LOCAL_WRITE_FAILED_EVENT,
  bulkDelete,
  bulkGet,
  bulkKeys,
  bulkSet,
  isBulkStorageKey,
  isBulkStoreReady,
  type LocalWriteFailedDetail
} from '../offline/bulkStore'

// findings #70 / #75: large per-unit datasets live in IndexedDB once the bulk
// store is hydrated (see offline/bulkStore.ts); everything else stays here.
const useBulk = (key: string): boolean => isBulkStoreReady() && isBulkStorageKey(key)

const reportLocalWriteFailure = (key: string, error: unknown): void => {
  if (!hasWindow() || typeof window.dispatchEvent !== 'function') return
  const message = error instanceof Error ? error.message : String(error)
  window.dispatchEvent(new CustomEvent<LocalWriteFailedDetail>(LOCAL_WRITE_FAILED_EVENT, { detail: { key, message } }))
}

const hasWindow = (): boolean => typeof window !== 'undefined'
const TASK_STORAGE_KEY = 'tasks:v1'
const TASK_STORAGE_KEY_LEGACY = 'sunday.tasks.v1'

const taskKey = (tenantKey = getActiveTenantKey()): string => getTenantScopedKey(TASK_STORAGE_KEY, tenantKey)
const legacyTaskKey = (tenantKey = getActiveTenantKey()): string => getTenantScopedKey(TASK_STORAGE_KEY_LEGACY, tenantKey)

type TasksPayloadV1 = {
  version: 1
  items: Task[]
}

const defaultReminder: TaskReminderSettings = {
  enabled: true,
  cadenceMinutes: 1440,
  remindCollaborators: false,
  remindResponsible: true
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
)

const cloneTasks = (tasks: Task[]): Task[] => JSON.parse(JSON.stringify(tasks)) as Task[]

const isQuotaExceededError = (error: unknown): boolean => {
  if (!error) return false
  const anyError = error as any
  const name = typeof anyError?.name === 'string' ? anyError.name : ''
  if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED') return true
  const code = anyError?.code
  if (code === 22 || code === 1014) return true
  const message = typeof anyError?.message === 'string' ? anyError.message : ''
  return /quota/i.test(message)
}

const listKeys = (): string[] => {
  if (!hasWindow()) return []
  const keys: string[] = []
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i)
    if (key) keys.push(key)
  }
  return keys
}

// Best-effort eviction of non-critical caches so important writes (like production tables)
// have a chance to succeed when localStorage is near quota.
const evictOptionalCaches = (tenantKey = getActiveTenantKey()): void => {
  if (!hasWindow()) return

  const candidates = [
    // Context mirrors (Dexie is canonical).
    getTenantScopedKey('farms', tenantKey),
    getTenantScopedKey('lots:v1', tenantKey),
    getTenantScopedKey('tasks:v1', tenantKey),
    // Default-tenant legacy fallbacks.
    tenantKey === 'default' ? 'farms' : null,
    tenantKey === 'default' ? 'lots:v1' : null,
    tenantKey === 'default' ? 'tasks:v1' : null
  ].filter((key): key is string => Boolean(key))

  candidates.forEach((key) => {
    try {
      window.localStorage.removeItem(key)
    } catch {
      // ignore
    }
  })

  // Remove per-context lot roster caches (regenerates from API/Dexie).
  const scopedSuffix = `::${tenantKey}`
  const keys = listKeys()
  keys.forEach((key) => {
    if (!key.endsWith(scopedSuffix) && !(tenantKey === 'default' && !key.includes('::'))) return
    if (key.startsWith('lots-roster:')) {
      try {
        window.localStorage.removeItem(key)
      } catch {
        // ignore
      }
    }
  })

  // Production table legacy backup blobs can be extremely large. If any exist,
  // remove them first so important writes (like production edits) can succeed.
  keys.forEach((key) => {
    if (!key.endsWith(scopedSuffix) && !(tenantKey === 'default' && !key.includes('::'))) return
    if (key.startsWith('productionUnitTableData-backup-')) {
      try {
        window.localStorage.removeItem(key)
      } catch {
        // ignore
      }
    }
  })
}

const safeSetItem = (key: string, value: string): void => {
  if (!hasWindow()) return
  if (useBulk(key)) {
    bulkSet(key, value)
    return
  }
  try {
    window.localStorage.setItem(key, value)
  } catch (error) {
    if (isQuotaExceededError(error)) {
      // Try freeing a bit of space, then retry once.
      try {
        evictOptionalCaches(getActiveTenantKey())
        window.localStorage.setItem(key, value)
        return
      } catch (retryError) {
        console.warn(`[storage] Failed to write key "${key}" after eviction`, retryError)
        // findings #70: a dropped local write must be visible, not only logged.
        reportLocalWriteFailure(key, retryError)
        return
      }
    }
    console.warn(`[storage] Failed to serialize key "${key}"`, error)
  }
}

const normalizeStrings = (values: unknown[]): string[] => {
  const items = values
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean)
  return Array.from(new Set(items))
}

const normalizeReminder = (value: unknown): TaskReminderSettings => {
  if (!isPlainObject(value)) {
    return { ...defaultReminder }
  }
  return {
    enabled: typeof value.enabled === 'boolean' ? value.enabled : defaultReminder.enabled,
    cadenceMinutes: typeof value.cadenceMinutes === 'number' && Number.isFinite(value.cadenceMinutes)
      ? value.cadenceMinutes
      : defaultReminder.cadenceMinutes,
    remindCollaborators: typeof value.remindCollaborators === 'boolean'
      ? value.remindCollaborators
      : defaultReminder.remindCollaborators,
    remindResponsible: typeof value.remindResponsible === 'boolean'
      ? value.remindResponsible
      : defaultReminder.remindResponsible
  }
}

const normalizeTaskImport = (candidate: unknown): Task | null => {
  if (!isPlainObject(candidate)) return null

  const idValue = candidate.id
  const titleValue = candidate.title
  if (typeof idValue !== 'string' || idValue.trim().length === 0) return null
  if (typeof titleValue !== 'string' || titleValue.trim().length === 0) return null

  const collaboratorIds = Array.isArray(candidate.collaboratorIds)
    ? normalizeStrings(candidate.collaboratorIds)
    : []

  const tags = Array.isArray(candidate.tags) ? normalizeStrings(candidate.tags) : undefined
  const metadata = candidate.metadata && isPlainObject(candidate.metadata)
    ? { ...candidate.metadata }
    : undefined

  const createdAt = typeof candidate.createdAt === 'string' ? candidate.createdAt : new Date().toISOString()
  const updatedAt = typeof candidate.updatedAt === 'string' ? candidate.updatedAt : createdAt

  return {
    id: idValue.trim(),
    title: titleValue.trim(),
    description: typeof candidate.description === 'string' ? candidate.description : undefined,
    assigneeId: typeof candidate.assigneeId === 'string' ? candidate.assigneeId : undefined,
    collaboratorIds,
    dueAt: typeof candidate.dueAt === 'string' ? candidate.dueAt : undefined,
    completed: Boolean(candidate.completed),
    completedAt: typeof candidate.completedAt === 'string' ? candidate.completedAt : undefined,
    createdAt,
    updatedAt,
    parentId: typeof candidate.parentId === 'string' ? candidate.parentId : undefined,
    reminderSettings: normalizeReminder(candidate.reminderSettings),
    lastReminderAt: typeof candidate.lastReminderAt === 'string' ? candidate.lastReminderAt : undefined,
    lastNotifiedAt: typeof candidate.lastNotifiedAt === 'string' ? candidate.lastNotifiedAt : undefined,
    tags,
    metadata
  }
}

const mergeTasks = (existing: Task | undefined, incoming: Task): Task => {
  if (!existing) return incoming

  return {
    ...existing,
    ...incoming,
    description: incoming.description !== undefined ? incoming.description : existing.description,
    assigneeId: incoming.assigneeId !== undefined ? incoming.assigneeId : existing.assigneeId,
    dueAt: incoming.dueAt !== undefined ? incoming.dueAt : existing.dueAt,
    parentId: incoming.parentId !== undefined ? incoming.parentId : existing.parentId,
    completed: incoming.completed,
    completedAt: incoming.completedAt !== undefined ? incoming.completedAt : existing.completedAt,
    reminderSettings: incoming.reminderSettings ?? existing.reminderSettings,
    lastReminderAt: incoming.lastReminderAt !== undefined ? incoming.lastReminderAt : existing.lastReminderAt,
    lastNotifiedAt: incoming.lastNotifiedAt !== undefined ? incoming.lastNotifiedAt : existing.lastNotifiedAt,
    tags: incoming.tags !== undefined ? incoming.tags : existing.tags,
    metadata: incoming.metadata !== undefined ? incoming.metadata : existing.metadata,
    collaboratorIds: incoming.collaboratorIds,
    createdAt: existing.createdAt,
    updatedAt: incoming.updatedAt ?? new Date().toISOString()
  }
}

/**
 * Safely parse a JSON value from localStorage. Returns the provided fallback when parsing fails
 * or when the key does not exist. This centralizes error handling and keeps components lighter.
 */
export function loadJSON<T>(key: string, fallback: T): T {
  if (!hasWindow()) return fallback
  try {
    const raw = useBulk(key) ? bulkGet(key) : window.localStorage.getItem(key)
    if (!raw) return fallback
    return JSON.parse(raw) as T
  } catch (error) {
    console.warn(`[storage] Failed to parse key "${key}"`, error)
    return fallback
  }
}

/** Persist a JSON-serializable value to localStorage with consistent error handling. */
export function saveJSON<T>(key: string, value: T): void {
  if (!hasWindow()) return
  try {
    safeSetItem(key, JSON.stringify(value))
  } catch (error) {
    console.warn(`[storage] Failed to serialize key "${key}"`, error)
  }
}

/** Remove a key from localStorage with a guard for SSR environments. */
export function deleteKey(key: string): void {
  if (!hasWindow()) return
  if (useBulk(key)) {
    bulkDelete(key)
    return
  }
  window.localStorage.removeItem(key)
}

/** Write a raw string value (routed like `saveJSON`: bulk store or localStorage). */
export function writeItem(key: string, value: string): void {
  safeSetItem(key, value)
}

/** Read a raw string value from localStorage with SSR safety. */
export function readItem(key: string): string | null {
  if (!hasWindow()) return null
  if (useBulk(key)) return bulkGet(key)
  return window.localStorage.getItem(key)
}

/**
 * Every stored key: localStorage plus the IndexedDB-backed bulk store. Use this
 * (not `window.localStorage.key(i)`) when scanning for dataset keys.
 */
export function listStorageKeys(): string[] {
  if (!hasWindow()) return []
  const keys = new Set<string>(listKeys())
  if (isBulkStoreReady()) bulkKeys().forEach((key) => keys.add(key))
  return Array.from(keys)
}

export function loadTasks(): Task[] {
  const tenantKey = getActiveTenantKey()
  const primary = loadJSON<unknown>(taskKey(tenantKey), null)
  if (primary && typeof primary === 'object' && !Array.isArray(primary)) {
    const payload = primary as Partial<TasksPayloadV1>
    if (payload.version === 1 && Array.isArray(payload.items)) {
      return cloneTasks(payload.items as Task[])
    }
  }

  // Legacy format: stored directly as an array under a namespaced key.
  const legacy = loadJSON<unknown>(legacyTaskKey(tenantKey), null)
  if (Array.isArray(legacy)) {
    const cloned = cloneTasks(legacy as Task[])
    saveTasks(cloned)
    return cloned
  }

  // Back-compat fallback: if default tenant has no scoped data yet, read old global keys.
  if (tenantKey === 'default') {
    const globalPrimary = loadJSON<unknown>(TASK_STORAGE_KEY, null)
    if (globalPrimary && typeof globalPrimary === 'object' && !Array.isArray(globalPrimary)) {
      const payload = globalPrimary as Partial<TasksPayloadV1>
      if (payload.version === 1 && Array.isArray(payload.items)) {
        const cloned = cloneTasks(payload.items as Task[])
        saveTasks(cloned)
        return cloned
      }
    }

    const globalLegacy = loadJSON<unknown>(TASK_STORAGE_KEY_LEGACY, null)
    if (Array.isArray(globalLegacy)) {
      const cloned = cloneTasks(globalLegacy as Task[])
      saveTasks(cloned)
      return cloned
    }
  }

  return []
}

export function saveTasks(tasks: Task[]): void {
  const tenantKey = getActiveTenantKey()
  const cloned = cloneTasks(tasks)
  const payload: TasksPayloadV1 = { version: 1, items: cloned }
  saveJSON(taskKey(tenantKey), payload)
  // Keep legacy key in sync for backwards compatibility.
  saveJSON(legacyTaskKey(tenantKey), cloned)
}

export function exportTasks(): { blob: Blob; count: number } {
  const tasks = loadTasks()
  const payload = {
    version: 1,
    exportedAt: new Date().toISOString(),
    count: tasks.length,
    items: tasks
  }

  if (typeof Blob !== 'function') {
    throw new Error('Blob is not supported in this environment.')
  }

  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
  return { blob, count: tasks.length }
}

export function importTasks(raw: string): { imported: number; skipped: number } {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    throw new Error('Import payload must be valid JSON.')
  }

  const candidates = Array.isArray(parsed)
    ? parsed
    : isPlainObject(parsed) && Array.isArray(parsed.items)
      ? parsed.items
      : null

  if (!candidates) {
    throw new Error('Import payload must include an items array.')
  }

  const existing = loadTasks()
  const taskMap = new Map(existing.map((task) => [task.id, task]))
  const seenInPayload = new Set<string>()

  let imported = 0
  let skipped = 0

  for (const candidate of candidates) {
    const normalized = normalizeTaskImport(candidate)
    if (!normalized) {
      skipped += 1
      continue
    }

    if (seenInPayload.has(normalized.id)) {
      skipped += 1
      continue
    }

    seenInPayload.add(normalized.id)
    const merged = mergeTasks(taskMap.get(normalized.id), normalized)
    taskMap.set(merged.id, merged)
    imported += 1
  }

  const finalTasks = Array.from(taskMap.values())
  const validIds = new Set(finalTasks.map((task) => task.id))

  finalTasks.forEach((task) => {
    if (task.parentId && !validIds.has(task.parentId)) {
      task.parentId = undefined
    }
  })

  finalTasks.sort((a, b) => a.updatedAt.localeCompare(b.updatedAt))

  saveTasks(finalTasks)

  return { imported, skipped }
}
